import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactFacebookLoginInfo } from 'react-facebook-login';
import type { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import logSignUpScreenDismissed from '@alltrails/amplitude/events/logSignUpScreenDismissed';
import type { User } from '@alltrails/types/User';
import PurchaseTier from '@alltrails/core/types/PurchaseTier';
import useSetUser from '@alltrails/context/hooks/useSetUser';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import logWebSignUpModalShown from '@alltrails/amplitude/events/logWebSignUpModalShown';
import logWebSignUpModalLogInTapped from '@alltrails/amplitude/events/logWebSignUpModalLogInTapped';
import { ModalStep, MultiStepModal, useMultiStepModal } from '@alltrails/modal';
import InstallInterstitial from '@alltrails/install-prompt/components/InstallInterstitial';
import useInstallPrompt from '@alltrails/install-prompt/hooks/useInstallPrompt';
import CheckoutModalStep from '@alltrails/upsell/components/CheckoutModalStep/CheckoutModalStep';
import CheckoutWelcome from '@alltrails/upsell/components/CheckoutWelcome/CheckoutWelcome';
import GenericPostAuthUpsellStep from '@alltrails/upsell/components/GenericPostAuthUpsell/PostAuthUpsellStep';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import { useDispatch } from 'react-redux';
import { setPlans } from '@alltrails/context/slices/plansSlice';
import { setCurrencyCode } from '@alltrails/context/slices/currencyCodeSlice';
import logLogInScreenContinueButtonTapped from '@alltrails/amplitude/events/logLogInScreenContinueButtonTapped';
import { useExperiments } from '@alltrails/experiments';
import getDefaultTierSelected from '@alltrails/upsell/components/UpsellSystem/GenericUpsell/getDefaultTierSelected';
import { useIntl } from 'react-intl';
import useGoogleHandler from '../../hooks/useGoogleHandler';
import useFacebookHandler from '../../hooks/useFacebookHandler';
import LoginResponse from '../../types/LoginResponse';
import getInstallInterstitialVariantByTrigger from '../../utils/getInstallInterstitialVariantByTrigger';
import useApplePopupHandler from '../../hooks/useApplePopupHandler';
import PlusFeatureTriggers from './PlusFeatureTriggers';
import LogIn from './LogIn';
import Initial from './Initial';
import Register from './Register';
import styles from './SignUpModal.module.scss';

type SignUpModalProps = {
  autoClose?: boolean;
  deeplinkData?: DeeplinkData;
  closeModal: (event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent) => void;
  heading?: ReactNode;
  hideInstallPrompt?: boolean;
  isOpen: boolean;
  onSuccess: (user: User) => void;
  redirectUrl?: string;
  hideUpsell?: boolean;
  trigger: CarouselDisplayTrigger;
  upsellTrigger: UpsellTrigger;
};

// Desktop: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=251%3A16532
// Mobile: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=250%3A16053

const SignUpModal = ({
  autoClose,
  closeModal,
  heading,
  hideInstallPrompt = false,
  isOpen,
  onSuccess,
  redirectUrl,
  hideUpsell = false,
  trigger,
  deeplinkData,
  upsellTrigger
}: SignUpModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const applePopupHandler = useApplePopupHandler(isOpen);
  const facebookHandler = useFacebookHandler();
  const googleHandler = useGoogleHandler();
  const dispatch = useDispatch();
  const experiments = useExperiments();
  const isApplePaySDKEnabled = experiments['web-growth-rm-apple-pay-sdk']?.value === 'treatment';
  const defaultTierSelected = getDefaultTierSelected();

  const { activeModal, navigateTo, goBack } = useMultiStepModal<'checkout' | 'initial' | 'login' | 'register' | 'upsell' | 'welcome'>('initial');
  const setUser = useSetUser();
  const intl = useIntl();
  const [isInterstitialOpen, setIsInterstitialOpen] = useInstallPrompt('interstitial', false, trigger === 'scroll auth');

  const [tierSelected, setTierSelected] = useState<PurchaseTier>(defaultTierSelected);

  const successCallback = useCallback(
    async ({ user, plans, currencyCode }: LoginResponse) => {
      if (redirectUrl) {
        window.location.assign(redirectUrl);
      } else {
        setUser(user);
        dispatch(setPlans(plans));
        dispatch(setCurrencyCode(currencyCode));

        global.google?.accounts.id.cancel(); // google can be undefined on mobile web

        const isInUpsellTier = user?.subscriptionTier === 'base';
        const shouldShowUpsell = !hideUpsell && isInUpsellTier && (!trigger || !PlusFeatureTriggers.includes(trigger));

        if (shouldShowUpsell) {
          navigateTo('upsell');
        } else {
          onSuccess(user);
          autoClose && closeModal();
        }
      }
    },
    [redirectUrl, setUser, hideUpsell, trigger, dispatch, navigateTo, onSuccess, autoClose, closeModal]
  );

  const handleCloseRequest = () => {
    logSignUpScreenDismissed({ signup_screen_dismissed_type: 'x' });
    closeModal();
  };

  const getLoginHandlers = useCallback(
    (isLogIn: boolean) => {
      const appleLoginHandler = async () => {
        if (isLogIn) {
          logLogInScreenContinueButtonTapped({ login_screen_continue_button_type: 'apple' });
        } else {
          logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'apple' });
        }

        try {
          const loginResponse = await applePopupHandler({ handlerType: isLogIn ? 'login' : 'signup' });
          successCallback(loginResponse);
        } catch {
          // no specific UI provided by design
        }
      };

      const googleLoginHandler = async (response: GoogleLoginResponse | GoogleLoginResponseOffline) => {
        const loginResponse = await googleHandler({ googleUserOrToken: response, handlerType: isLogIn ? 'login' : 'signup' });
        successCallback(loginResponse);
      };

      const facebookLoginHandler = async (response: ReactFacebookLoginInfo) => {
        try {
          const loginResponse = await facebookHandler({
            loginInfo: response,
            handlerType: isLogIn ? 'login' : 'signup'
          });
          successCallback(loginResponse);
        } catch {
          // no specific UI provided by design
        }
      };

      return { appleLoginHandler, googleLoginHandler, facebookLoginHandler };
    },
    [applePopupHandler, facebookHandler, googleHandler, successCallback]
  );

  function getActiveModal() {
    switch (activeModal) {
      case 'login':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <LogIn handleLoginSuccess={successCallback} {...getLoginHandlers(true)} />
          </ModalStep>
        );

      case 'register':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <Register
              onLogInClick={() => {
                logWebSignUpModalLogInTapped();
                navigateTo('login');
              }}
              onSuccess={successCallback}
            />
          </ModalStep>
        );

      case 'initial':
        return (
          <ModalStep onCloseRequest={handleCloseRequest}>
            <Initial
              heading={heading}
              onCreateAccountClick={() => {
                navigateTo('register');
                logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'email' });
              }}
              onLogInClick={() => {
                logWebSignUpModalLogInTapped();
                navigateTo('login');
              }}
              {...getLoginHandlers(false)}
            />
          </ModalStep>
        );

      case 'upsell':
        return (
          <GenericPostAuthUpsellStep
            navigateTo={navigateTo}
            onCloseRequest={closeModal}
            onSuccess={onSuccess}
            tierSelected={tierSelected}
            setTierSelected={setTierSelected}
            upsellTrigger={upsellTrigger}
          />
        );

      case 'checkout':
        return (
          <CheckoutModalStep
            closeModal={closeModal}
            navigateToWelcome={() => navigateTo('welcome')}
            purchaseTier={tierSelected}
            trigger={upsellTrigger}
          />
        );

      case 'welcome':
        return (
          <CheckoutWelcome
            closeModal={user => {
              onSuccess(user);
              closeModal();
            }}
            purchaseTier={tierSelected}
          />
        );
    }
  }

  useEffect(() => {
    if (isOpen) {
      logWebSignUpModalShown({ trigger });
      if (!hideInstallPrompt) {
        setIsInterstitialOpen(true);
      }
    }
  }, [isOpen]);

  const ariaLabel = useMemo(() => {
    switch (activeModal) {
      case 'login':
        return intl.formatMessage({ defaultMessage: 'Log in' });
      case 'register':
        return intl.formatMessage({ defaultMessage: 'Sign up' });
      case 'upsell':
        return intl.formatMessage({ defaultMessage: 'Upgrade' });
      case 'welcome':
        return tierSelected === 'peak'
          ? intl.formatMessage({ defaultMessage: 'Welcome to Peak' })
          : intl.formatMessage({ defaultMessage: 'Welcome to Plus' });
      case 'checkout':
        return intl.formatMessage({ defaultMessage: 'Upgrade' });
      default:
        return intl.formatMessage({ defaultMessage: 'Sign up' });
    }
  }, [activeModal, intl, tierSelected]);

  return (
    <MultiStepModal
      ariaLabel={ariaLabel}
      testId="signup-modal"
      backgroundClassName={styles.background}
      ref={modalRef}
      initialFocus={isInterstitialOpen ? false : undefined}
      isFullHeight={activeModal === 'checkout'}
      isOpen={isOpen}
      onBackRequest={activeModal === 'upsell' || activeModal === 'welcome' ? undefined : goBack}
      isPaused={isInterstitialOpen || (isApplePaySDKEnabled && activeModal === 'checkout')}
      onCloseRequest={closeModal}
      focusTrapOptions={{ allowOutsideClick: e => e.target !== modalRef.current, clickOutsideDeactivates: false }} // DISCO-10566 don't close the signup modal when install banner and signup modal both appear and user clicks outside of banner
      {...(activeModal === 'upsell' || activeModal === 'checkout' || activeModal === 'welcome' ? { modalClassName: styles.taller } : {})}
    >
      {!hideInstallPrompt && (
        <InstallInterstitial
          preserveInterstitial={false}
          isOpen={isInterstitialOpen}
          onRequestClose={() => setIsInterstitialOpen(false)}
          promptLocation="trail page"
          deeplinkData={deeplinkData}
          displayTrigger={trigger}
          variant={getInstallInterstitialVariantByTrigger(trigger)}
        />
      )}
      {getActiveModal()}
    </MultiStepModal>
  );
};

export default SignUpModal;
